import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles, Zap, Radio, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';

export type SkinType = 'default' | 'cyberpunk' | 'anime' | 'stalker';

interface SkinTransitionOverlayProps {
  active: boolean;
  targetSkin: SkinType;
  tone?: 'yellow-cyan' | 'cyan-magenta';
  lang?: string;
}

export const SkinTransitionOverlay: React.FC<SkinTransitionOverlayProps> = ({
  active,
  targetSkin,
  tone = 'yellow-cyan',
  lang = 'en'
}) => {
  const isUa = lang === 'ua' || lang === 'uk';

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100000] pointer-events-none flex items-center justify-center overflow-hidden"
        >
          {/* S.T.A.L.K.E.R. Animation */}
          {targetSkin === 'stalker' && (
            <motion.div 
              initial={{ scale: 1.05, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/95 flex flex-col items-center justify-center"
            >
              {/* Scanlines & CRT flicker */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.75)_50%)] bg-[length:100%_4px] pointer-events-none opacity-80" />
              <div className="absolute inset-0 bg-radial from-transparent via-black/40 to-black pointer-events-none" />
              
              {/* Crosshair grid */}
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                className="w-72 h-72 rounded-full border border-[#4ade80]/30 flex items-center justify-center relative"
              >
                <div className="w-48 h-48 rounded-full border border-dashed border-[#4ade80]/40" />
                <div className="absolute inset-x-0 top-1/2 h-[1px] bg-[#4ade80]/30" />
                <div className="absolute inset-y-0 left-1/2 w-[1px] bg-[#4ade80]/30" />
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative z-10 text-center font-mono mt-6 space-y-2 px-4"
              >
                <div className="flex items-center justify-center gap-2 text-[#4ade80]">
                  <Radio className="w-5 h-5 animate-pulse" />
                  <span className="text-xs font-black tracking-widest uppercase">
                    {isUa ? "PDA ЗАВАНТАЖЕННЯ..." : "ZONE PDA INITIALIZING..."}
                  </span>
                </div>
                <h2 className="text-3xl font-black text-[#4ade80] tracking-widest drop-shadow-[0_0_12px_rgba(74,222,128,0.7)]">
                  S.T.A.L.K.E.R.
                </h2>
                <p className="text-xs text-[#4ade80]/70 font-mono tracking-wide">
                  {isUa ? "Біометричний зв'язок встановлено. Вдалих логів, Сталкере." : "Biometric link established. Good hunting, Stalker."}
                </p>
              </motion.div>
            </motion.div>
          )}

          {/* Cyberpunk Animation */}
          {targetSkin === 'cyberpunk' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#050512]/95 flex flex-col items-center justify-center"
            >
              {/* Matrix grid & glitch lines */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(252,238,10,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,204,0.04)_1px,transparent_1px)] bg-[size:24px_24px]" />
              
              {/* High speed scan line */}
              <motion.div 
                initial={{ top: '-10%' }}
                animate={{ top: '110%' }}
                transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
                className={cn(
                  "absolute left-0 right-0 h-1 blur-[1px]",
                  tone === 'cyan-magenta' ? "bg-[#00f0ff] shadow-[0_0_20px_#00f0ff]" : "bg-[#FCEE0A] shadow-[0_0_20px_#FCEE0A]"
                )}
              />

              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 350, damping: 20 }}
                className="relative z-10 text-center font-mono space-y-3 px-4"
              >
                <div className="flex items-center justify-center gap-2">
                  <Zap className={cn(
                    "w-5 h-5 animate-bounce",
                    tone === 'cyan-magenta' ? "text-[#ff0055]" : "text-[#FCEE0A]"
                  )} />
                  <span className={cn(
                    "text-xs font-black tracking-widest uppercase",
                    tone === 'cyan-magenta' ? "text-[#00f0ff]" : "text-[#00ffcc]"
                  )}>
                    {isUa ? "НЕЙРОННИЙ ЗВ'ЯЗОК // РЕСИНХРОНІЗАЦІЯ" : "NEURAL LINK // RESYNC"}
                  </span>
                </div>
                <h2 className={cn(
                  "text-3xl font-black tracking-widest uppercase",
                  tone === 'cyan-magenta' 
                    ? "text-[#00f0ff] drop-shadow-[0_0_15px_rgba(0,240,255,0.8)]" 
                    : "text-[#FCEE0A] drop-shadow-[0_0_15px_rgba(252,238,10,0.8)]"
                )}>
                  CYBERPUNK
                </h2>
                <div className="inline-block px-3 py-1 bg-black/60 border border-zinc-700 text-[11px] text-zinc-300">
                  {tone === 'cyan-magenta' ? "TONE: CYAN & MAGENTA" : "TONE: NEON YELLOW & CYAN"}
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Anime Animation */}
          {targetSkin === 'anime' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-br from-[#1b0d2a]/95 via-[#150a24]/95 to-[#240b28]/95 flex flex-col items-center justify-center"
            >
              <motion.div 
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: [0, 1.4, 1.2], opacity: [0, 0.8, 0.5] }}
                transition={{ duration: 0.6 }}
                className="w-96 h-96 rounded-full bg-gradient-to-tr from-pink-500/30 to-purple-600/30 blur-3xl absolute"
              />

              <motion.div
                initial={{ y: 25, scale: 0.9, opacity: 0 }}
                animate={{ y: 0, scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 22 }}
                className="relative z-10 text-center space-y-3 px-4"
              >
                <div className="flex items-center justify-center gap-2 text-pink-300">
                  <Sparkles className="w-5 h-5 animate-spin" />
                  <span className="text-xs font-bold tracking-widest uppercase">
                    {isUa ? "ТРАНСФОРМАЦІЯ СКІНА ✨" : "SKIN TRANSCENDENCE ✨"}
                  </span>
                </div>
                <h2 className="text-3xl font-black italic tracking-wider text-pink-400 drop-shadow-[0_2px_15px_rgba(236,72,153,0.7)]">
                  『超アニメ』 EDITION
                </h2>
                <p className="text-xs text-pink-200/80 font-medium">
                  {isUa ? "Квітни як сакура, перемагай у квестах! 🌸" : "Blossom like sakura, conquer your quests! 🌸"}
                </p>
              </motion.div>
            </motion.div>
          )}

          {/* Default Animation */}
          {targetSkin === 'default' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-zinc-950/90 backdrop-blur-md flex flex-col items-center justify-center"
            >
              <motion.div 
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: [0.4, 1.2, 1], opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="w-40 h-40 rounded-full border-2 border-emerald-500/40 flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.3)]"
              >
                <div className="w-24 h-24 rounded-full border border-emerald-500/60 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                </div>
              </motion.div>

              <motion.div
                initial={{ y: 15, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative z-10 text-center space-y-2 mt-6 px-4"
              >
                <h2 className="text-2xl font-black tracking-wide text-white">
                  DEFAULT THEME
                </h2>
                <p className="text-xs text-zinc-400 font-medium">
                  {isUa ? "Чистий та збалансований інтерфейс відновлено." : "Clean & minimalist experience restored."}
                </p>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
